type TemporalLike = {
  Now?: {
    instant?: () => { epochMilliseconds: number }
    zonedDateTimeISO?: (timeZone?: string) => {
      year: number
      month: number
      day: number
      hour: number
      minute: number
      second: number
      millisecond?: number
      offsetNanoseconds?: number
      add?: (value: { minutes?: number; hours?: number }) => unknown
      epochSeconds?: number
      epochMilliseconds?: number
    }
  }
}

type DateTimeParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
  millisecond: number
}

function getTemporal(): TemporalLike | null {
  const maybeTemporal = (globalThis as { Temporal?: TemporalLike }).Temporal
  return maybeTemporal ?? null
}

function pad(value: number, width = 2) {
  return String(value).padStart(width, '0')
}

function fromDate(date: Date, useUtc: boolean): DateTimeParts {
  return {
    year: useUtc ? date.getUTCFullYear() : date.getFullYear(),
    month: (useUtc ? date.getUTCMonth() : date.getMonth()) + 1,
    day: useUtc ? date.getUTCDate() : date.getDate(),
    hour: useUtc ? date.getUTCHours() : date.getHours(),
    minute: useUtc ? date.getUTCMinutes() : date.getMinutes(),
    second: useUtc ? date.getUTCSeconds() : date.getSeconds(),
    millisecond: useUtc ? date.getUTCMilliseconds() : date.getMilliseconds()
  }
}

export function getNowInstantEpochMilliseconds() {
  const temporal = getTemporal()
  const instant = temporal?.Now?.instant?.()
  if (instant) {
    return instant.epochMilliseconds
  }

  return Date.now()
}

export function getNowParts(options?: { local?: boolean; epochMilliseconds?: number }) {
  const local = options?.local ?? false
  const epochMilliseconds = options?.epochMilliseconds
  const temporal = getTemporal()

  if (epochMilliseconds != null) {
    return fromDate(new Date(epochMilliseconds), !local)
  }

  if (local) {
    const zoned = temporal?.Now?.zonedDateTimeISO?.()
    if (zoned) {
      return {
        year: zoned.year,
        month: zoned.month,
        day: zoned.day,
        hour: zoned.hour,
        minute: zoned.minute,
        second: zoned.second,
        millisecond: zoned.millisecond ?? 0
      }
    }
  }

  return fromDate(new Date(), !local)
}

export function formatIsoDate(parts: Pick<DateTimeParts, 'year' | 'month' | 'day'>) {
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
}

export function formatIsoDateTime(parts: Omit<DateTimeParts, 'millisecond'>) {
  return `${formatIsoDate(parts)} ${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`
}

export function formatIsoDateTimeMs(parts: DateTimeParts) {
  return `${formatIsoDateTime(parts)}.${String(parts.millisecond).padStart(3, '0')}`
}

export function parseDatePartsWithPattern(value: string, pattern: string): DateTimeParts {
  const tokens = ['yyyy', 'yy', 'MM', 'M', 'dd', 'd', 'HH', 'mm', 'ss'] as const
  const sortedTokens = [...tokens].sort((a, b) => b.length - a.length)
  let regex = '^'

  for (let index = 0; index < pattern.length;) {
    const token = sortedTokens.find(item => pattern.startsWith(item, index))
    if (token) {
      regex += `(?<${token}>\\d{${token.length === 1 ? '1,2' : token.length}})`
      index += token.length
      continue
    }

    const char = pattern[index] ?? ''
    regex += char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    index += 1
  }

  regex += '$'
  const match = new RegExp(regex).exec(value)
  if (!match?.groups) {
    throw new Error(`value does not match pattern ${pattern}`)
  }

  return {
    year: match.groups.yyyy ? Number(match.groups.yyyy) : match.groups.yy ? 2000 + Number(match.groups.yy) : 1970,
    month: match.groups.MM || match.groups.M ? Number(match.groups.MM || match.groups.M) : 1,
    day: match.groups.dd || match.groups.d ? Number(match.groups.dd || match.groups.d) : 1,
    hour: match.groups.HH ? Number(match.groups.HH) : 0,
    minute: match.groups.mm ? Number(match.groups.mm) : 0,
    second: match.groups.ss ? Number(match.groups.ss) : 0,
    millisecond: 0
  }
}

export function formatDatePartsWithPattern(parts: DateTimeParts, pattern: string) {
  let result = pattern
  const values = {
    yyyy: String(parts.year),
    yy: String(parts.year).slice(-2),
    MM: pad(parts.month),
    M: String(parts.month),
    dd: pad(parts.day),
    d: String(parts.day),
    HH: pad(parts.hour),
    mm: pad(parts.minute),
    ss: pad(parts.second)
  }

  for (const token of ['yyyy', 'yy', 'MM', 'M', 'dd', 'd', 'HH', 'mm', 'ss'] as const) {
    result = result.replaceAll(token, values[token])
  }

  return result
}

function toDate(parts: DateTimeParts, utc = true) {
  return utc
    ? new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, parts.millisecond))
    : new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, parts.millisecond)
}

function fromUtcDate(date: Date) {
  return fromDate(date, true)
}

export function parseBestEffortDateParts(value: string) {
  for (const pattern of ['yyyy-MM-dd HH:mm:ss', 'yyyy-MM-ddTHH:mm:ss', 'yyyy-MM-dd', 'dd/MM/yyyy HH:mm:ss', 'dd/MM/yyyy']) {
    try {
      return parseDatePartsWithPattern(value, pattern)
    } catch {
      continue
    }
  }

  const direct = new Date(value)
  if (!Number.isNaN(direct.getTime())) {
    return fromUtcDate(direct)
  }

  throw new Error('unable to parse datetime input')
}

export function shiftDatePartsByUtcOffset(parts: DateTimeParts, offsetHours: number) {
  const date = toDate(parts, true)
  date.setUTCHours(date.getUTCHours() + offsetHours)
  return fromUtcDate(date)
}

export function datePartsToUnixSeconds(parts: DateTimeParts) {
  return Math.floor(toDate(parts, true).getTime() / 1000)
}

export function unixSecondsToDateParts(value: number) {
  return fromUtcDate(new Date(value * 1000))
}

export function addMinutesToNowParts(minutes: number) {
  const date = new Date(getNowInstantEpochMilliseconds())
  date.setUTCMinutes(date.getUTCMinutes() + minutes)
  return fromUtcDate(date)
}
